import React, { useEffect, useState } from "react";
import PaginationComp from "../components/PaginationComp"; // your existing one
import api from "../utils/api";

const ProductSection = ({ slug, selectedCategory, addToCart, onCustomise }) => {
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");


  const limit = 12;

  /* ================= FETCH PRODUCTS ================= */
  const fetchProducts = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/api/public/restaurant/${slug}/products`, {
        params: {
          search: debouncedSearch,
          foodType: typeFilter === "ALL" ? "all" : typeFilter.toLowerCase(),
          page,
          limit,
          ...(selectedCategory && { category: selectedCategory }),
        },
      });

      console.log("Products:", res.data.products);

      setProducts(res.data.products || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error("Product fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= REFRESH WHEN DEPENDENCIES CHANGE ================= */
  useEffect(() => {
    setPage(1);
  }, [selectedCategory, debouncedSearch, typeFilter]);

  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(searchQuery);
  }, 400);

  return () => clearTimeout(timer);
}, [searchQuery]);


  useEffect(() => {
  fetchProducts();
}, [selectedCategory, debouncedSearch, typeFilter, page]);


  return (
    <div className="p-4 sm:p-6 flex flex-col h-full">
      {/* ================= TOP CONTROLS ================= */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        {/* 🔍 Search */}
        <input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-full md:w-1/3"
        />

        {/* Veg / Non-Veg Filter */}
        <div className="flex gap-2">
          {["ALL", "VEG", "NON-VEG"].map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                typeFilter === type
                  ? "bg-teal-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {type === "ALL"
                ? "All"
                : type === "VEG"
                  ? "🥦 Veg"
                  : "🍗 Non-Veg"}
            </button>
          ))}
        </div>
      </div>

      {/* ================= PRODUCTS ================= */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : products.length === 0 ? (
          <p className="text-center text-gray-500 mt-10">No products found</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {products.map((product) => (
              <div
                key={product._id}
                onClick={() => {
                  if (product.isAvailable === false) return;
                  const isCustomizable = (product.variants && product.variants.length > 0) || (product.addOns && product.addOns.length > 0);
                  if (isCustomizable && onCustomise) {
                    onCustomise(product);
                  } else {
                    addToCart(product);
                  }
                }}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition overflow-hidden relative cursor-pointer ${
                  product.isAvailable === false ? "opacity-60 grayscale-[50%] cursor-not-allowed" :
                  ((product.variants && product.variants.length > 0) || (product.addOns && product.addOns.length > 0)) ? "border border-dashed border-teal-200" : ""
                }`}
              >
                <div className="relative h-40 bg-gray-100">
                  <img
                    src={product.image || "/default-product.png"}
                    alt={product.productName}
                    onError={(e) => { e.target.src = "/default-product.png"; }}
                    className="w-full h-full object-cover"
                  />

                  {product.type && product.type.toLowerCase() !== "not_defined" && (
                    <span
                      className={`absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded-full text-white ${
                        product.type.toLowerCase() === "veg"
                          ? "bg-green-600"
                          : product.type.toLowerCase() === "non-veg"
                            ? "bg-red-600"
                            : "bg-gray-600"
                      }`}
                    >
                      {product.type.toLowerCase() === "veg" ? "Veg" : product.type.toLowerCase() === "non-veg" ? "Non-Veg" : product.type}
                    </span>
                  )}
                  {product.isAvailable === false && (
                    <div className="absolute inset-0 bg-white/40 flex items-center justify-center backdrop-blur-[1px]">
                      <span className="bg-red-600 text-white px-3 py-1 font-bold text-xs uppercase tracking-widest rounded-md shadow-lg transform -rotate-6">Unavailable</span>
                    </div>
                  )}
                </div>

                <div className="p-3">
                  <h3 className="text-sm font-semibold break-words leading-tight">
                    {product.productName}
                  </h3>

                  <div className="flex justify-between items-center mt-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col">
                      <span className="text-teal-600 font-bold text-sm">
                        ₹ {product.price}
                      </span>
                      {((product.variants && product.variants.length > 0) || (product.addOns && product.addOns.length > 0)) && (
                        <span className="text-[10px] text-teal-600 font-bold mt-0.5">
                          Customizable
                        </span>
                      )}
                    </div>

                    {((product.variants && product.variants.length > 0) || (product.addOns && product.addOns.length > 0)) ? (
                      <button
                        disabled={product.isAvailable === false}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.isAvailable === false) return;
                          if (onCustomise) onCustomise(product);
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-bold transition flex items-center gap-0.5 shadow-sm ${
                          product.isAvailable === false 
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                            : "bg-teal-600 hover:bg-teal-700 text-white"
                        }`}
                      >
                        <span>+</span>
                        <span>Customise</span>
                      </button>
                    ) : (
                      <button
                        disabled={product.isAvailable === false}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.isAvailable === false) return;
                          addToCart(product);
                        }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-serif transition ${
                          product.isAvailable === false
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-teal-600 hover:bg-teal-700 text-white"
                        }`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {totalPages > 1 && (
        <div className="mt-6">
          <PaginationComp
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSection;
